import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import {
  DollarSign, Users, TrendingUp, Copy, CheckCircle,
  Clock, Settings, Loader2,
  BarChart3, Calendar, AlertCircle, Star
} from 'lucide-react'
import SEO from '@/components/layout/SEO'

interface CommissionTier {
  id: string
  tier_key: string
  label: string
  commission_rate: number
  min_referrals: number
  sort_order: number
}

const T = {
  navy: '#1a2740',
  navyMid: '#243452',
  sage: '#4a7c5e',
  sageDark: '#2d5a3d',
  sageLight: '#e8f2ec',
  cream: '#faf7f2',
  white: '#ffffff',
  gold: '#c9903a',
  goldLight: '#fdf6e9',
  slate: '#475569',
  muted: '#8a94a6',
  border: '#e2e8f0',
  green: '#10b981',
  red: '#ef4444',
}

const heroGrad = `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`
const sageGrad = `linear-gradient(135deg, ${T.sage}, ${T.sageDark})`

// ─── Types ─────────────────────────────────────────────────────────────────────
interface Partner {
  id: string; code: string; full_name: string; email: string
  status: string; commission_rate: number
  total_active_referrals: number; total_earned_cents: number
  total_paid_cents: number; pending_payout_cents: number
  payout_method: string | null; payout_details: any
  approved_at: string | null; created_at: string
  subscriber_floor_active?: boolean
}
interface Referral {
  id: string; plan_key: string; platform: string; status: string
  monthly_value_cents: number; commission_rate_at_referral: number
  first_payment_at: string | null; last_payment_at: string | null
  cancelled_at: string | null; created_at: string
}
interface LedgerRow {
  id: string; period_month: string; gross_revenue_cents: number
  commission_rate: number; commission_cents: number
  status: string; created_at: string
}
interface Payout {
  id: string; period_month: string; total_cents: number
  status: string; payout_method: string | null
  payout_reference: string | null; paid_at: string | null; created_at: string
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (cents: number) =>
  `$${(cents / 100).toFixed(2)}`

function StatCard({ icon, label, value, sub, color = T.sage }: {
  icon: React.ReactNode; label: string; value: string; sub?: string; color?: string
}) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      style={{
        background: T.white, borderRadius: 18, padding: '22px 20px',
        border: `1px solid ${T.border}`, boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
        flex: 1, minWidth: 160,
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</div>
        <span style={{ fontSize: 10, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 900, color: T.navy }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>{sub}</div>}
    </motion.div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; color: string }> = {
    active: { bg: '#dcfce7', color: '#166534' },
    cancelled: { bg: '#fee2e2', color: '#991b1b' },
    paused: { bg: '#fef3c7', color: '#92400e' },
    pending: { bg: '#e0e7ff', color: '#3730a3' },
    approved: { bg: '#dcfce7', color: '#166534' },
    paid: { bg: '#dcfce7', color: '#166534' },
    processing: { bg: '#fef3c7', color: '#92400e' },
    failed: { bg: '#fee2e2', color: '#991b1b' },
  }
  const c = colors[status] || { bg: T.sageLight, color: T.sageDark }
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
      letterSpacing: '0.06em', padding: '3px 10px', borderRadius: 20,
      background: c.bg, color: c.color,
    }}>{status}</span>
  )
}

// ─── Milestone Progress Bar — fully dynamic from DB tiers ─────────────────────
function MilestoneBar({ activeReferrals, tiers, partner }: { activeReferrals: number; tiers: CommissionTier[]; partner: Partner }) {
  const { t } = useTranslation()

  if (tiers.length === 0) return null

  // Sort ascending by min_referrals
  const sorted = [...tiers].sort((a, b) => a.min_referrals - b.min_referrals)
  const currentIdx = sorted.reduce((best, tier, idx) =>
    activeReferrals >= tier.min_referrals ? idx : best, 0)
  const current = sorted[currentIdx]
  const next = sorted[currentIdx + 1]
  const maxReferrals = sorted[sorted.length - 1].min_referrals || 1
  const pct = next
    ? Math.min(100, ((activeReferrals - current.min_referrals) / (next.min_referrals - current.min_referrals)) * 100)
    : 100

  return (
    <div style={{ background: T.white, borderRadius: 18, padding: '24px 20px', border: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h4 style={{ fontWeight: 800, color: T.navy, margin: 0, fontSize: 15 }}>{t('affiliates.dashboard.milestone.title')}</h4>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.sage }}>
          {t('affiliates.dashboard.milestone.current', { rate: current.commission_rate })}
          {(partner as any).subscriber_floor_active && (
            <span style={{ fontSize: 11, color: T.gold, fontWeight: 700, marginLeft: 8, background: T.goldLight, padding: '2px 8px', borderRadius: 8 }}>🎁 Subscriber Bonus Active</span>
          )}
        </span>
      </div>

      {/* Track */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <div style={{ height: 8, background: T.sageLight, borderRadius: 8, overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            style={{ height: '100%', background: sageGrad, borderRadius: 8 }}
          />
        </div>
        {/* Tier markers (skip first since it starts at 0) */}
        {sorted.slice(1).map((tier, i) => {
          const leftPct = Math.min(100, (tier.min_referrals / Math.max(maxReferrals, 1)) * 100)
          return (
            <div key={i} style={{
              position: 'absolute', top: -4, left: `${leftPct}%`,
              width: 16, height: 16, borderRadius: '50%',
              background: activeReferrals >= tier.min_referrals ? T.sage : T.sageLight,
              border: `3px solid ${T.white}`,
              transform: 'translateX(-50%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {activeReferrals >= tier.min_referrals && <CheckCircle size={8} color={T.white} />}
            </div>
          )
        })}
      </div>

      {(partner as any).subscriber_floor_active ? (
        <p style={{ fontSize: 13, color: T.slate, margin: 0 }}>
          You're earning the top rate (30%) while your subscription is active. Referrals you earn now are yours to keep even if your subscription ends — {activeReferrals} real referral{activeReferrals === 1 ? '' : 's'} so far.
        </p>
      ) : next ? (
        <p style={{ fontSize: 13, color: T.slate, margin: 0 }}>
          {t('affiliates.dashboard.milestone.next', {
            needed: next.min_referrals - activeReferrals,
            rate: next.commission_rate,
          })}
        </p>
      ) : (
        <p style={{ fontSize: 13, color: T.sage, fontWeight: 700, margin: 0 }}>
          🏆 {t('affiliates.dashboard.milestone.maxed')}
        </p>
      )}
    </div>
  )
}

// ─── Payout Settings Form ──────────────────────────────────────────────────────
function PayoutSettings({ partner, onSave }: { partner: Partner; onSave: () => void }) {
  const { t } = useTranslation()
  const [method, setMethod] = useState(partner.payout_method || '')
  const [details, setDetails] = useState<Record<string, string>>(partner.payout_details || {})
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      await supabase.functions.invoke('affiliate-payout', {
        body: {
          action: 'update_payout_settings',
          payout_method: method,
          payout_details: details,
        },
      })
      setSaved(true)
      setTimeout(() => { setSaved(false); onSave() }, 1500)
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
          {t('affiliates.dashboard.payout.method')}
        </label>
        <select value={method} onChange={e => setMethod(e.target.value)}
          style={{ width: '100%', height: 46, padding: '0 14px', borderRadius: 10, border: `2px solid ${T.border}`, fontSize: 15, background: T.white, color: T.navy }}>
          <option value="">{t('affiliates.dashboard.payout.selectMethod')}</option>
          <option value="paypal">{t('affiliates.apply.payoutMethods.paypal')}</option>
          <option value="bank_transfer">{t('affiliates.apply.payoutMethods.bank_transfer')}</option>
          <option value="wise">{t('affiliates.apply.payoutMethods.wise')}</option>
        </select>
      </div>

      {method === 'paypal' && (
        <div>
          <label style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
            {t('affiliates.dashboard.payout.paypalEmail')}
          </label>
          <input type="email" value={details.paypal_email || ''} onChange={e => setDetails(p => ({ ...p, paypal_email: e.target.value }))}
            placeholder="your@paypal.com"
            style={{ width: '100%', height: 46, padding: '0 14px', borderRadius: 10, border: `2px solid ${T.border}`, fontSize: 15, boxSizing: 'border-box' }} />
        </div>
      )}

      {method === 'bank_transfer' && (
        <>
          <div>
            <label style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
              {t('affiliates.dashboard.payout.accountName')}
            </label>
            <input type="text" value={details.account_name || ''} onChange={e => setDetails(p => ({ ...p, account_name: e.target.value }))}
              placeholder={t('affiliate.full_name_as_it_appears_on')}
              style={{ width: '100%', height: 46, padding: '0 14px', borderRadius: 10, border: `2px solid ${T.border}`, fontSize: 15, boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
                Country
              </label>
              <select value={details.bank_country || ''} onChange={e => setDetails(p => ({ ...p, bank_country: e.target.value }))}
                style={{ width: '100%', height: 46, padding: '0 14px', borderRadius: 10, border: `2px solid ${T.border}`, fontSize: 15, background: T.white, color: T.navy }}>
                <option value="">{t('affiliate.select_country')}</option>
                <option value="AU">Australia</option>
                <option value="NZ">{t('affiliate.new_zealand')}</option>
                <option value="US">{t('affiliate.united_states')}</option>
                <option value="GB">{t('affiliate.united_kingdom')}</option>
                <option value="CA">Canada</option>
                <option value="SG">Singapore</option>
                <option value="OTHER">{t('affiliate.other_use_swift_iban')}</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
                Currency
              </label>
              <select value={details.bank_currency || ''} onChange={e => setDetails(p => ({ ...p, bank_currency: e.target.value }))}
                style={{ width: '100%', height: 46, padding: '0 14px', borderRadius: 10, border: `2px solid ${T.border}`, fontSize: 15, background: T.white, color: T.navy }}>
                <option value="">{t('affiliate.same_as_account')}</option>
                <option value="AUD">AUD</option>
                <option value="NZD">NZD</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="SGD">SGD</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
                BSB / Routing / ABA number
              </label>
              <input type="text" value={details.bank_code || ''} onChange={e => setDetails(p => ({ ...p, bank_code: e.target.value }))}
                placeholder={t('affiliate.au_digit_bsb_us_digit_routing')}
                style={{ width: '100%', height: 46, padding: '0 14px', borderRadius: 10, border: `2px solid ${T.border}`, fontSize: 15, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
                Account number
              </label>
              <input type="text" value={details.account_number || ''} onChange={e => setDetails(p => ({ ...p, account_number: e.target.value }))}
                placeholder={t('affiliate.account_iban_number')}
                style={{ width: '100%', height: 46, padding: '0 14px', borderRadius: 10, border: `2px solid ${T.border}`, fontSize: 15, boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
                SWIFT / BIC <span style={{ opacity: 0.5, fontWeight: 400 }}>(international)</span>
              </label>
              <input type="text" value={details.swift_bic || ''} onChange={e => setDetails(p => ({ ...p, swift_bic: e.target.value }))}
                placeholder="e.g. WPACAU2S"
                style={{ width: '100%', height: 46, padding: '0 14px', borderRadius: 10, border: `2px solid ${T.border}`, fontSize: 15, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
                Bank name
              </label>
              <input type="text" value={details.bank_name || ''} onChange={e => setDetails(p => ({ ...p, bank_name: e.target.value }))}
                placeholder="e.g. Westpac / Chase / DBS"
                style={{ width: '100%', height: 46, padding: '0 14px', borderRadius: 10, border: `2px solid ${T.border}`, fontSize: 15, boxSizing: 'border-box' }} />
            </div>
          </div>
          <p style={{ fontSize: 11.5, color: T.muted, margin: 0, lineHeight: 1.5 }}>
            AUD payouts are sent by domestic transfer (BSB + account). International payouts are sent by SWIFT — include the SWIFT/BIC and your account's IBAN (in the account number field for EU/UK/Asia). Wise covers 60+ countries with just your email.
          </p>
        </>
      )}

      {method === 'wise' && (
        <div>
          <label style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
            {t('affiliates.dashboard.payout.wiseEmail')}
          </label>
          <input type="email" value={details.wise_email || ''} onChange={e => setDetails(p => ({ ...p, wise_email: e.target.value }))}
            placeholder="your@wise.com"
            style={{ width: '100%', height: 46, padding: '0 14px', borderRadius: 10, border: `2px solid ${T.border}`, fontSize: 15, boxSizing: 'border-box' }} />
        </div>
      )}

      <button onClick={handleSave} disabled={saving || !method}
        style={{
          padding: '13px', borderRadius: 11, background: saved ? T.green : sageGrad,
          color: T.white, border: 'none', fontWeight: 700, fontSize: 15,
          cursor: saving ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'background 0.3s',
        }}>
        {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : saved ? <CheckCircle size={16} /> : null}
        {saved ? t('affiliates.dashboard.payout.saved') : t('affiliates.dashboard.payout.save')}
      </button>
    </div>
  )
}

// ─── Affiliate Code Settings Form ──────────────────────────────────────────────
function AffiliateCodeSettings({ partner, onSave }: { partner: Partner; onSave: () => void }) {
  const { t } = useTranslation()
  const [code, setCode] = useState(partner.code)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    if (code.trim().length < 3) {
      setError('Code must be at least 3 characters')
      return
    }
    setError('')
    setSaving(true)
    try {
      const res = await supabase.functions.invoke('affiliate-payout', {
        body: {
          action: 'update_affiliate_code',
          new_code: code.trim().toUpperCase(),
        },
      })
      if (res.error || res.data?.error) throw new Error(res.data?.error || 'Failed to update code')
      setSaved(true)
      setTimeout(() => { setSaved(false); onSave() }, 1500)
    } catch (err: any) {
      setError(err.message || 'Error saving code')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
          {t('affiliates.dashboard.settings.affiliateCode', 'Custom Affiliate Code')}
        </label>
        <p style={{ fontSize: 13, color: T.slate, marginBottom: 12 }}>
          {t('affiliates.dashboard.settings.codeNote', 'Change your unique identifier used in referral links. Cannot contain spaces or special characters.')}
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <input type="text" value={code} onChange={e => {
            setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))
            setError('')
          }}
            placeholder="e.g. MARCUS99"
            style={{ flex: 1, minWidth: 0, height: 46, padding: '0 14px', borderRadius: 10, border: `2px solid ${error ? T.red : T.border}`, fontSize: 15, fontFamily: 'monospace' }} />
          <button onClick={handleSave} disabled={saving || code === partner.code || code.length < 3}
            style={{
              padding: '0 24px', borderRadius: 11, background: saved ? T.green : sageGrad,
              color: T.white, border: 'none', fontWeight: 700, fontSize: 15, minWidth: 100,
              cursor: (saving || code === partner.code || code.length < 3) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'background 0.3s', opacity: (code === partner.code || code.length < 3) ? 0.6 : 1,
            }}>
            {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : saved ? <CheckCircle size={16} /> : null}
            {saved ? t('affiliates.dashboard.payout.saved') : t('affiliates.dashboard.payout.save')}
          </button>
        </div>
        {error && <div style={{ fontSize: 13, color: T.red, marginTop: 8, fontWeight: 600 }}>{error}</div>}

        {/* ── Share without a link — podcast/group-chat copy ── */}
        <div style={{ marginTop: 8, padding: '14px 16px', background: T.cream, borderRadius: 10, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t('affiliate.podcast_group_chat_drop')}</p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: T.slate, fontStyle: 'italic' }}>
              "Go to stillwatersacademy.com and add code <strong style={{ color: T.sage }}>{partner.code}</strong> at checkout for 5% off."
            </p>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(`Go to stillwatersacademy.com and add code ${partner.code} at checkout for 5% off.`)
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            }}
            style={{ padding: '8px 16px', borderRadius: 10, border: `1px solid ${T.sage}40`, background: T.sageLight, color: T.sage, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {copied ? <CheckCircle size={13} /> : <Copy size={13} />} {copied ? 'Copied!' : 'Copy line'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function AffiliateDashboardPage() {
  const { t } = useTranslation()
  const { user, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()

  const [partner, setPartner] = useState<Partner | null>(null)
  const [referrals, setReferrals] = useState<Referral[]>([])
  const [ledger, setLedger] = useState<LedgerRow[]>([])
  const [payouts, setPayouts] = useState<Payout[]>([])
  const [tiers, setTiers] = useState<CommissionTier[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'overview' | 'referrals' | 'earnings' | 'payouts' | 'settings'>('overview')
  const [copied, setCopied] = useState(false)
  const [notAffiliate, setNotAffiliate] = useState(false)

  const loadDashboard = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      const [dashRes, tiersRes] = await Promise.all([
        supabase.functions.invoke('affiliate-payout', { body: { action: 'dashboard' } }),
        supabase.from('affiliate_commission_tiers').select('*').eq('is_active', true).order('sort_order', { ascending: true }),
      ])
      if (dashRes.error) throw dashRes.error
      if (!dashRes.data?.partner) { setNotAffiliate(true); return }
      setPartner(dashRes.data.partner)
      setReferrals(dashRes.data.referrals || [])
      setLedger(dashRes.data.ledger || [])
      setPayouts(dashRes.data.payouts || [])
      if (tiersRes.data && tiersRes.data.length > 0) setTiers(tiersRes.data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { if (user) loadDashboard() }, [user, loadDashboard])

  const copyLink = (platform: 'sage' | 'university') => {
    if (!partner) return
    const url = platform === 'sage'
      ? `https://stillwatersacademy.com?ref=${partner.code}`
      : `https://stillwatersuniversity.com?ref=${partner.code}`
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (authLoading || loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.cream }}>
        <Loader2 size={36} color={T.sage} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    )
  }

  if (!user) {
    navigate('/auth/login?next=/affiliates/dashboard')
    return null
  }

  if (notAffiliate) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.cream, padding: 24 }}>
        <SEO title="Affiliate Access" noindex />
        <div style={{ textAlign: 'center', maxWidth: 440 }}>
          <AlertCircle size={48} color={T.muted} style={{ marginBottom: 16, display: 'block', margin: '0 auto 16px' }} />
          <h2 style={{ fontSize: 26, fontWeight: 900, color: T.navy, marginBottom: 12 }}>
            {t('affiliates.dashboard.notAffiliate.title')}
          </h2>
          <p style={{ color: T.slate, marginBottom: 28, lineHeight: 1.6 }}>
            {t('affiliates.dashboard.notAffiliate.body')}
          </p>
          <button onClick={() => navigate('/affiliates/apply')}
            style={{ padding: '14px 28px', background: sageGrad, color: T.white, border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 16, cursor: 'pointer' }}>
            {t('affiliates.dashboard.notAffiliate.cta')}
          </button>
        </div>
      </div>
    )
  }

  if (!partner) return null

  if (partner.status === 'pending') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.cream, padding: 24 }}>
        <SEO title="Affiliate Application Pending" noindex />
        <div style={{ textAlign: 'center', maxWidth: 440 }}>
          <Clock size={48} color={T.gold} style={{ display: 'block', margin: '0 auto 16px' }} />
          <h2 style={{ fontSize: 26, fontWeight: 900, color: T.navy, marginBottom: 12 }}>
            {t('affiliates.dashboard.pending.title')}
          </h2>
          <p style={{ color: T.slate, marginBottom: 20, lineHeight: 1.6 }}>
            {t('affiliates.dashboard.pending.body')}
          </p>
          <div style={{ background: T.goldLight, borderRadius: 14, padding: '16px 20px', border: `1px solid ${T.gold}30` }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', marginBottom: 4 }}>
              {t('affiliates.apply.provisionalCode')}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: T.navy }}>{partner.code}</div>
          </div>
        </div>
      </div>
    )
  }

  const TABS = [
    { id: 'overview' as const, label: t('affiliates.dashboard.tabs.overview'), icon: <BarChart3 size={14} /> },
    { id: 'referrals' as const, label: t('affiliates.dashboard.tabs.referrals'), icon: <Users size={14} /> },
    { id: 'earnings' as const, label: t('affiliates.dashboard.tabs.earnings'), icon: <TrendingUp size={14} /> },
    { id: 'payouts' as const, label: t('affiliates.dashboard.tabs.payouts'), icon: <DollarSign size={14} /> },
    { id: 'settings' as const, label: t('affiliates.dashboard.tabs.settings'), icon: <Settings size={14} /> },
  ]

  return (
    <div style={{ minHeight: '100vh', background: T.cream }}>
      <SEO title="Affiliate Dashboard" noindex />

      {/* Hero */}
      <div style={{ background: heroGrad }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 28 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: T.sage, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                {t('affiliates.dashboard.heroLabel')}
              </div>
              <h1 style={{ fontSize: 24, fontWeight: 900, color: T.white, margin: 0 }}>
                {t('affiliates.dashboard.welcome', { name: partner.full_name.split(' ')[0] })}
              </h1>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: '8px 14px', border: '1px solid rgba(255,255,255,0.12)' }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 2 }}>{t('affiliates.dashboard.yourCode')}</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: T.white, letterSpacing: '0.06em' }}>{partner.code}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(['sage', 'university'] as const).map(p => (
                  <button key={p} onClick={() => copyLink(p)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                      background: 'rgba(74,124,94,0.25)', border: '1px solid rgba(74,124,94,0.4)',
                      borderRadius: 8, color: T.sage, fontWeight: 700, fontSize: 12, cursor: 'pointer',
                    }}>
                    {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
                    {p === 'sage' ? 'Sage' : 'University'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Summary stats */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <StatCard icon={<DollarSign size={18} />} label={t('affiliates.dashboard.stats.pending')} value={fmt(partner.pending_payout_cents)} sub={t('affiliates.dashboard.stats.pendingSub')} color={T.gold} />
            <StatCard icon={<TrendingUp size={18} />} label={t('affiliates.dashboard.stats.totalEarned')} value={fmt(partner.total_earned_cents)} color={T.sage} />
            <StatCard icon={<Users size={18} />} label={t('affiliates.dashboard.stats.activeReferrals')} value={String(partner.total_active_referrals)} color={T.navy} />
            <StatCard icon={<Star size={18} />} label={t('affiliates.dashboard.stats.rate')} value={`${partner.commission_rate}%`} sub={(partner as any).subscriber_floor_active ? 'Subscriber bonus active 🎁' : t('affiliates.dashboard.stats.rateSub')} color="#8b5cf6" />
          </div>
        </div>
        <svg viewBox="0 0 1440 32" style={{ display: 'block', width: '100%', height: 32 }} preserveAspectRatio="none">
          <path d="M0,32 C360,0 1080,32 1440,12 L1440,32 Z" fill={T.cream} />
        </svg>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '12px 24px 80px' }}>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 28, background: T.white, borderRadius: 14, padding: 4, border: `1px solid ${T.border}`, overflowX: 'auto' }}>
          {TABS.map(t_ => (
            <button key={t_.id} onClick={() => setTab(t_.id)}
              style={{
                flex: 1, minWidth: 100, padding: '9px 14px', borderRadius: 10, border: 'none',
                cursor: 'pointer', fontSize: 13, fontWeight: tab === t_.id ? 700 : 500,
                background: tab === t_.id ? sageGrad : 'transparent',
                color: tab === t_.id ? T.white : T.muted,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'all 0.15s', whiteSpace: 'nowrap',
              }}>
              {t_.icon} {t_.label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ── */}
        {tab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <MilestoneBar activeReferrals={partner.total_active_referrals} tiers={tiers} partner={partner} />

            {/* Referral links */}
            <div style={{ background: T.white, borderRadius: 18, padding: '24px 20px', border: `1px solid ${T.border}` }}>
              <h4 style={{ fontWeight: 800, color: T.navy, margin: '0 0 16px', fontSize: 15 }}>
                {t('affiliates.dashboard.links.title')}
              </h4>
              {(['sage', 'university'] as const).map(platform => {
                const url = platform === 'sage'
                  ? `https://stillwatersacademy.com?ref=${partner.code}`
                  : `https://stillwatersuniversity.com?ref=${partner.code}`
                return (
                  <div key={platform} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                    background: T.cream, borderRadius: 10, marginBottom: 10,
                    border: `1px solid ${T.border}`,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', marginBottom: 2 }}>
                        {platform === 'sage' ? 'Sage Wisdom' : 'Still Waters University'}
                      </div>
                      <div style={{ fontSize: 13, color: T.slate, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</div>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
                      style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${T.sage}40`, background: T.sageLight, color: T.sage, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
                      {t('affiliates.dashboard.links.copy')}
                    </button>
                  </div>
                )
              })}
            </div>

            {/* Recent activity */}
            {ledger.length > 0 && (
              <div style={{ background: T.white, borderRadius: 18, padding: '24px 20px', border: `1px solid ${T.border}` }}>
                <h4 style={{ fontWeight: 800, color: T.navy, margin: '0 0 16px', fontSize: 15 }}>
                  {t('affiliates.dashboard.recentActivity')}
                </h4>
                {ledger.slice(0, 5).map(row => (
                  <div key={row.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 0', borderBottom: `1px solid ${T.border}`,
                  }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>
                        {new Date(row.period_month).toLocaleDateString('en', { month: 'long', year: 'numeric' })}
                      </div>
                      <div style={{ fontSize: 11, color: T.muted }}>{row.commission_rate}% commission</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontWeight: 800, color: T.sage, fontSize: 16 }}>{fmt(row.commission_cents)}</span>
                      <StatusBadge status={row.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── REFERRALS ── */}
        {tab === 'referrals' && (
          <div style={{ background: T.white, borderRadius: 18, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
            <div style={{ padding: '20px 20px 0' }}>
              <h4 style={{ fontWeight: 800, color: T.navy, margin: '0 0 4px' }}>{t('affiliates.dashboard.referrals.title')}</h4>
              <p style={{ fontSize: 13, color: T.muted, margin: '0 0 16px' }}>{t('affiliates.dashboard.referrals.subtitle')}</p>
            </div>
            {referrals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 24px', color: T.muted }}>
                <Users size={36} style={{ opacity: 0.3, marginBottom: 12, display: 'block', margin: '0 auto 12px' }} />
                <p>{t('affiliates.dashboard.referrals.empty')}</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: T.cream }}>
                      {[
                        t('affiliates.dashboard.referrals.cols.plan'),
                        t('affiliates.dashboard.referrals.cols.platform'),
                        t('affiliates.dashboard.referrals.cols.monthlyValue'),
                        t('affiliates.dashboard.referrals.cols.commission'),
                        t('affiliates.dashboard.referrals.cols.status'),
                        t('affiliates.dashboard.referrals.cols.since'),
                      ].map(h => (
                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((ref, i) => (
                      <tr key={ref.id} style={{ borderTop: `1px solid ${T.border}`, background: i % 2 === 0 ? T.white : '#fcfcfe' }}>
                        <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: T.navy }}>{ref.plan_key}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: 11, background: ref.platform === 'sage' ? T.sageLight : T.goldLight, color: ref.platform === 'sage' ? T.sage : T.gold, padding: '3px 8px', borderRadius: 8, fontWeight: 700 }}>
                            {ref.platform}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: T.slate }}>{fmt(ref.monthly_value_cents)}/mo</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: T.sage }}>
                          {fmt(Math.round(ref.monthly_value_cents * (ref.commission_rate_at_referral / 100)))}/mo
                        </td>
                        <td style={{ padding: '12px 16px' }}><StatusBadge status={ref.status} /></td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: T.muted }}>
                          {ref.first_payment_at ? new Date(ref.first_payment_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── EARNINGS ── */}
        {tab === 'earnings' && (
          <div style={{ background: T.white, borderRadius: 18, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
            <div style={{ padding: '20px 20px 0' }}>
              <h4 style={{ fontWeight: 800, color: T.navy, margin: '0 0 4px' }}>{t('affiliates.dashboard.earnings.title')}</h4>
              <p style={{ fontSize: 13, color: T.muted, margin: '0 0 16px' }}>{t('affiliates.dashboard.earnings.subtitle')}</p>
            </div>
            {ledger.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 24px', color: T.muted }}>
                <TrendingUp size={36} style={{ opacity: 0.3, display: 'block', margin: '0 auto 12px' }} />
                <p>{t('affiliates.dashboard.earnings.empty')}</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: T.cream }}>
                      {[
                        t('affiliates.dashboard.earnings.cols.month'),
                        t('affiliates.dashboard.earnings.cols.revenue'),
                        t('affiliates.dashboard.earnings.cols.rate'),
                        t('affiliates.dashboard.earnings.cols.commission'),
                        t('affiliates.dashboard.earnings.cols.status'),
                      ].map(h => (
                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((row, i) => (
                      <tr key={row.id} style={{ borderTop: `1px solid ${T.border}`, background: i % 2 === 0 ? T.white : '#fcfcfe' }}>
                        <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: T.navy }}>
                          {new Date(row.period_month).toLocaleDateString('en', { month: 'long', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: T.slate }}>{fmt(row.gross_revenue_cents)}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: T.slate }}>{row.commission_rate}%</td>
                        <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 800, color: T.sage }}>{fmt(row.commission_cents)}</td>
                        <td style={{ padding: '12px 16px' }}><StatusBadge status={row.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── PAYOUTS ── */}
        {tab === 'payouts' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: T.sageLight, borderRadius: 14, padding: '16px 20px', display: 'flex', gap: 12, alignItems: 'center' }}>
              <DollarSign size={20} color={T.sage} />
              <div>
                <div style={{ fontWeight: 800, color: T.sageDark, fontSize: 15 }}>
                  {t('affiliates.dashboard.payouts.pendingBalance', { amount: fmt(partner.pending_payout_cents) })}
                </div>
                <div style={{ fontSize: 12, color: T.sage, marginTop: 2 }}>
                  {t('affiliates.dashboard.payouts.paidNote', { amount: fmt(partner.total_paid_cents) })}
                </div>
              </div>
            </div>

            <div style={{ background: T.white, borderRadius: 18, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
              <div style={{ padding: '20px 20px 0' }}>
                <h4 style={{ fontWeight: 800, color: T.navy, margin: '0 0 16px' }}>{t('affiliates.dashboard.payouts.history')}</h4>
              </div>
              {payouts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 24px', color: T.muted }}>
                  <Calendar size={36} style={{ opacity: 0.3, display: 'block', margin: '0 auto 12px' }} />
                  <p>{t('affiliates.dashboard.payouts.empty')}</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: T.cream }}>
                        {[
                          t('affiliates.dashboard.payouts.cols.period'),
                          t('affiliates.dashboard.payouts.cols.amount'),
                          t('affiliates.dashboard.payouts.cols.method'),
                          t('affiliates.dashboard.payouts.cols.reference'),
                          t('affiliates.dashboard.payouts.cols.status'),
                          t('affiliates.dashboard.payouts.cols.date'),
                        ].map(h => (
                          <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map((p, i) => (
                        <tr key={p.id} style={{ borderTop: `1px solid ${T.border}`, background: i % 2 === 0 ? T.white : '#fcfcfe' }}>
                          <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: T.navy }}>
                            {new Date(p.period_month).toLocaleDateString('en', { month: 'long', year: 'numeric' })}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 800, color: T.sage }}>{fmt(p.total_cents)}</td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: T.slate }}>{p.payout_method || '—'}</td>
                          <td style={{ padding: '12px 16px', fontSize: 12, color: T.muted, fontFamily: 'monospace' }}>{p.payout_reference || '—'}</td>
                          <td style={{ padding: '12px 16px' }}><StatusBadge status={p.status} /></td>
                          <td style={{ padding: '12px 16px', fontSize: 12, color: T.muted }}>
                            {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── SETTINGS ── */}
        {tab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ background: T.white, borderRadius: 18, padding: '24px 20px', border: `1px solid ${T.border}` }}>
              <h4 style={{ fontWeight: 800, color: T.navy, margin: '0 0 20px', fontSize: 15 }}>
                {t('affiliates.dashboard.payout.title')}
              </h4>
              <PayoutSettings partner={partner} onSave={loadDashboard} />
            </div>

            <div style={{ background: T.white, borderRadius: 18, padding: '24px 20px', border: `1px solid ${T.border}` }}>
              <h4 style={{ fontWeight: 800, color: T.navy, margin: '0 0 20px', fontSize: 15 }}>
                {t('affiliates.dashboard.settings.codeTitle', 'Referral Settings')}
              </h4>
              <AffiliateCodeSettings partner={partner} onSave={loadDashboard} />
            </div>

            <div style={{ background: T.white, borderRadius: 18, padding: '24px 20px', border: `1px solid ${T.border}` }}>
              <h4 style={{ fontWeight: 800, color: T.navy, margin: '0 0 16px', fontSize: 15 }}>
                {t('affiliates.dashboard.settings.accountInfo')}
              </h4>
              {[
                { label: t('affiliates.dashboard.settings.email'), value: partner.email },
                { label: t('affiliates.dashboard.settings.joinedDate'), value: new Date(partner.created_at).toLocaleDateString() },
                { label: t('affiliates.dashboard.settings.approvedDate'), value: partner.approved_at ? new Date(partner.approved_at).toLocaleDateString() : '—' },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 14, color: T.muted }}>{row.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
